import React from "react"

import { css } from "@emotion/react"
import type { RefSelectProps } from "antd"

import type {
    IHealthProvider,
    IPayer,
    ISimplifiedPractice,
} from "@common-shared/types/responseData/commonResponseData.types.ts"

import type {
    ISelectRawOption,
    ISelectRenderedOption,
} from "@front-shared/types/objects.types.ts"

import { NIRVANA_COLORS } from "../config/theme.config.ts"
import { PrimaryText } from "../elements/primaryText.element"

export class LookupHelper {
    /**
     * Get rendered health provider for autocomplete
     */
    static getRenderedHealthProvider(
        item: IHealthProvider,
        ignoreTitle = false,
        ignoreAddress = false,
        ignoreNpi = false,
        dropdownDisplayValueAdditionalPart?: string,
        ignorePreferredName = false,
    ): ISelectRenderedOption {
        const dropdownDisplayValueParts: string[] = []

        const dropdownDisplayValueAdditionalParts: string[] =
            dropdownDisplayValueAdditionalPart
                ? [dropdownDisplayValueAdditionalPart]
                : []

        const healthProviderName = ignorePreferredName
            ? item.name
            : item.preferredName || item.name

        if (healthProviderName) {
            dropdownDisplayValueParts.push(healthProviderName)
        }

        if (item.title && !ignoreTitle) {
            dropdownDisplayValueParts.push(
                LookupHelper.getCleanedHealthProviderDegree(item.title),
            )
        }

        if (item.address && !ignoreAddress) {
            dropdownDisplayValueAdditionalParts.push.apply(
                dropdownDisplayValueAdditionalParts,
                [item.address.city, item.address.state, item.address.zip],
            )
        }

        let dropdownDisplayValueAdditional =
            dropdownDisplayValueAdditionalParts.join(", ")

        // Negative IDs have item that we manually added (if npi is missing), so we don't want to duplicate NPI in view
        if (!ignoreNpi && item.id > 0) {
            dropdownDisplayValueAdditional = `NPI: ${item.npi}${
                dropdownDisplayValueAdditional
                    ? `<br />${dropdownDisplayValueAdditional}`
                    : ""
            }`
        }

        return {
            displayValue: healthProviderName,
            dropdownDisplayValue: dropdownDisplayValueParts.join(", "),
            dropdownDisplayValueAdditional,
            value: item,
        }
    }

    /**
     * Generate user defined health provider based on NPI
     */
    static getUserDefinedHealthProviderByNPI(npi: string): IHealthProvider {
        return {
            id: -1 * new Date().getTime(),
            name: `NPI: ${npi}`,
            npi,
            firstName: "Unknown",
            lastName: "Unknown",
            middleName: null,
            title: null,
            isEnrolled: false,
            updatedAt: new Date().getTime(),
            createdAt: new Date().getTime(),
        }
    }

    /**
     * Get custom autocomplete option that is based on user's search input
     */
    static getCustomSearchBasedAutocompleteOption(
        search: string,
    ): ISelectRenderedOption {
        return {
            key: (-1 * new Date().getTime()).toString(),
            displayValue: search,
            value: search,
        }
    }

    /**
     * Get rendered Payer (insurer) value for dropdown
     */
    static getRenderedPayer(item: IPayer): ISelectRenderedOption {
        return {
            displayValue: item.insuranceName,
            dropdownDisplayValue: item.insuranceName,
            dropdownDisplayValueAdditional: `ID ${item.payerId}`,
            value: item,
        }
    }

    /**
     * Get rendered Simplified Practice value for dropdown as ISelectRawOption
     */
    static getRenderedRawSimplifiedPractice(
        item: ISimplifiedPractice,
    ): ISelectRawOption {
        return {
            label: item.name,
            value: item.id,
        }
    }

    /**
     * Get rendered Simplified Practice value for dropdown as ISelectRenderedOption
     */
    static getRenderedSimplifiedPractice(
        item: ISimplifiedPractice,
    ): ISelectRenderedOption {
        const displayValue = item.name

        return {
            displayValue: displayValue,
            dropdownDisplayValue: displayValue,
            value: item,
        }
    }

    /**
     * Get cleaned health provider degree
     */
    static getCleanedHealthProviderDegree(degree: string): string {
        return degree.replace(/[^A-Z-]/g, "")
    }

    /**
     * Get autoComplete not found content block
     */
    static getNotFoundContent(
        title?: string,
        subtitle?: string,
        getActionContent?: (
            currentInputValue?: string,
        ) => React.JSX.Element | null,
        currentInputValue?: string,
        elementRef?: RefSelectProps | null,
    ): React.JSX.Element | null {
        if (!title && !subtitle && !getActionContent) {
            return null
        }

        return (
            <div
                className="flex items-center"
                css={[
                    css({
                        paddingTop: 22,
                        paddingRight: 12,
                        paddingBottom: 22,
                        paddingLeft: 38,
                        backgroundColor: NIRVANA_COLORS.brand.offWhite,
                        borderRadius: 8,
                        border: `1px solid ${NIRVANA_COLORS.brand.warmShadow}`,
                    }),
                ]}
            >
                {title || subtitle ? (
                    <div
                        className={`
                            flex-1
                            ${getActionContent ? "mr-[10px]" : ""}
                        `}
                    >
                        {title ? (
                            <PrimaryText typography="text">{title}</PrimaryText>
                        ) : null}

                        {subtitle ? (
                            <PrimaryText typography="caption">
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: subtitle,
                                    }}
                                />
                            </PrimaryText>
                        ) : null}
                    </div>
                ) : null}

                {getActionContent && (
                    <div onClick={elementRef?.blur}>
                        {getActionContent(currentInputValue)}
                    </div>
                )}
            </div>
        )
    }
}
